<template>
  <div class="page-root">
    <div class="d-flex align-center justify-space-between">
      <h1><span class="font-weight-bold">펫스토어</span></h1>
      <v-chip color="#fff3e0" text-color="#e8743b" class="font-weight-bold"
        >🥕 {{ currencyBalance.toLocaleString() }}</v-chip
      >
    </div>
    <p class="text-caption grey--text mb-2">펫에게 줄 음식과 꾸밀 악세사리</p>

    <v-tabs v-model="tab" class="mb-4" color="#8e6fd6" slider-color="#8e6fd6">
      <v-tab>🍖 음식 ({{ foods.length }})</v-tab>
      <v-tab>🧸 장난감 ({{ toys.length }})</v-tab>
      <v-tab>🏠 집 ({{ houses.length }})</v-tab>
      <v-tab>🖼️ 배경화면 ({{ backgrounds.length }})</v-tab>
      <v-tab>🎩 악세사리 ({{ accessories.length }})</v-tab>
      <v-tab>✨ 스티커</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- 음식 탭 -->
      <v-tab-item>
        <div class="store-grid">
          <v-card v-for="item in foods" :key="item.id || item.name" class="store-card" outlined hover>
            <div class="store-card__thumb">
              <v-img v-if="item.imageUrl" :src="item.imageUrl" :aspect-ratio="1" class="store-card__img" />
              <div v-else class="food-emoji">{{ item.emoji }}</div>
              <span v-if="ownedMap[item.id]" class="store-card__owned">보유 {{ ownedMap[item.id] }}</span>
            </div>
            <div class="store-card__body">
              <div>
                <div class="store-card__name">{{ item.name }}</div>
                <div class="food-restore">
                  <span class="food-restore__hunger">🍖 +{{ item.hunger }}</span>
                  <span class="food-restore__energy">⚡ +{{ item.energy }}</span>
                </div>
              </div>
              <div class="store-card__price">🥕 {{ item.price.toLocaleString() }}</div>
            </div>
            <div v-if="item.desc" class="store-card__desc">{{ item.desc }}</div>
            <div class="store-card__buy">
              <v-btn
                small
                block
                depressed
                color="#8e6fd6"
                class="white--text"
                :disabled="currencyBalance < item.price"
                @click="onBuyClick(item)"
              >
                {{ currencyBalance < item.price ? "🥕 부족" : "구매" }}
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-tab-item>

      <!-- 장난감 탭 -->
      <v-tab-item>
        <div class="store-grid">
          <v-card v-for="item in toys" :key="item.id || item.name" class="store-card" outlined hover>
            <div class="store-card__thumb">
              <v-img v-if="item.imageUrl" :src="item.imageUrl" :aspect-ratio="1" class="store-card__img" />
              <div v-else class="emoji-thumb emoji-thumb--toy">{{ item.emoji }}</div>
              <span v-if="ownedMap[item.id]" class="store-card__owned">보유 {{ ownedMap[item.id] }}</span>
            </div>
            <div class="store-card__body">
              <div>
                <div class="store-card__name">{{ item.name }}</div>
                <div class="food-restore">
                  <span class="toy-durability">🛠️ 내구도 {{ item.durability || "-" }}</span>
                </div>
              </div>
              <div class="store-card__price">🥕 {{ item.price.toLocaleString() }}</div>
            </div>
            <div v-if="item.desc" class="store-card__desc">{{ item.desc }}</div>
            <div class="store-card__buy">
              <v-btn
                small
                block
                depressed
                color="#8e6fd6"
                class="white--text"
                :disabled="currencyBalance < item.price"
                @click="onBuyClick(item)"
              >
                {{ currencyBalance < item.price ? "🥕 부족" : "구매" }}
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-tab-item>

      <!-- 집 탭 -->
      <v-tab-item>
        <div class="store-grid">
          <v-card v-for="item in houses" :key="item.name" class="store-card" outlined hover>
            <div class="emoji-thumb">{{ item.emoji }}</div>
            <div class="store-card__body">
              <div class="store-card__name">{{ item.name }}</div>
              <div class="store-card__price">🥕 {{ item.price.toLocaleString() }}</div>
            </div>
          </v-card>
        </div>
      </v-tab-item>

      <!-- 배경화면 탭 (라이브 lottie 미리보기 · 보유 기반) -->
      <v-tab-item>
        <div class="store-grid">
          <v-card v-for="item in backgrounds" :key="item.id" class="store-card" outlined hover>
            <div class="store-card__thumb bg-thumb">
              <lottie-preview :file-name="item.file_name" />
              <span v-if="ownedMap[item.id]" class="store-card__owned">보유</span>
            </div>
            <div class="store-card__body">
              <div class="store-card__name">{{ item.name }}</div>
              <div class="store-card__price">🥕 {{ item.price.toLocaleString() }}</div>
            </div>
            <div class="store-card__buy">
              <v-btn
                v-if="ownedMap[item.id]"
                small
                block
                depressed
                disabled
              >
                보유중
              </v-btn>
              <v-btn
                v-else
                small
                block
                depressed
                color="#8e6fd6"
                class="white--text"
                :disabled="currencyBalance < item.price"
                @click="onBuyClick(item)"
              >
                {{ currencyBalance < item.price ? "🥕 부족" : "구매" }}
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-tab-item>

      <!-- 악세사리 탭 -->
      <v-tab-item>
        <div class="store-grid">
          <v-card v-for="item in accessoryItems" :key="item.file" class="store-card" outlined hover>
            <v-img :src="item.src" :aspect-ratio="1" class="store-card__img" />
            <div class="store-card__body">
              <div class="store-card__name">{{ item.name }}</div>
              <div class="store-card__price">🥕 {{ item.price.toLocaleString() }}</div>
            </div>
          </v-card>
        </div>
      </v-tab-item>

      <!-- 스티커 탭 (준비 중) -->
      <v-tab-item>
        <div class="store-empty">✨ 스티커는 곧 만나요!</div>
      </v-tab-item>
    </v-tabs-items>

    <!-- 구매 확인 다이얼로그 -->
    <base-dialog v-model="buyDialog" title="구매 확인" :max-width="340">
      <div v-if="buyTarget">
        <div class="d-flex align-center mb-2">
          <span style="font-size: 40px; margin-right: 12px">
            <v-img v-if="buyTarget.imageUrl" :src="buyTarget.imageUrl" width="40" height="40" class="rounded" />
            <template v-else>{{ buyTarget.emoji }}</template>
          </span>
          <div>
            <div class="font-weight-bold">{{ buyTarget.name }}</div>
            <div class="text-caption grey--text">{{ buyTarget.desc }}</div>
          </div>
        </div>
        <!-- 수량 선택 (기본 1) — 배경은 비-스택이라 숨김 -->
        <div v-if="!buyTarget.isBackground" class="d-flex align-center justify-space-between my-3">
          <span class="font-weight-medium">수량</span>
          <div class="d-flex align-center">
            <v-btn icon small outlined :disabled="buyQty <= 1 || buying" @click="changeQty(-1)">
              <v-icon small>mdi-minus</v-icon>
            </v-btn>
            <span class="mx-3 font-weight-bold" style="min-width: 24px; text-align: center">{{ buyQty }}</span>
            <v-btn icon small outlined :disabled="buyQty >= maxQty || buying" @click="changeQty(1)">
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider class="my-2" />
        <div class="d-flex align-center justify-space-between buy-line">
          <span class="text-caption grey--text">합계</span>
          <strong :class="{ 'red--text': totalCost > currencyBalance }">🥕 {{ totalCost.toLocaleString() }}</strong>
        </div>
        <div class="d-flex align-center justify-space-between buy-line">
          <span class="text-caption grey--text">내 당근</span>
          <span>🥕 {{ currencyBalance.toLocaleString() }}</span>
        </div>
        <div class="d-flex align-center justify-space-between buy-line">
          <span class="text-caption grey--text">구매 후 잔액</span>
          <strong :class="totalCost > currencyBalance ? 'red--text' : 'remain-ok'">
            🥕 {{ (currencyBalance - totalCost).toLocaleString() }}
          </strong>
        </div>
      </div>

      <template #actions>
        <v-btn text :disabled="buying" @click="buyDialog = false">취소</v-btn>
        <v-btn
          color="#8e6fd6"
          class="white--text"
          :loading="buying"
          :disabled="totalCost > currencyBalance"
          @click="confirmBuy"
        >
          구매
        </v-btn>
      </template>
    </base-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import accessories from "./accessoriesManifest";
import houses from "./houseManifest";
import SupabaseApi from "@/api/SupabaseApi";
import CurrencyApi from "@/api/CurrencyApi";
import LottiePreview from "@/components-model/LottiePreview.vue";

export default {
  name: "PetStorePage",
  components: { LottiePreview },
  data() {
    return {
      tab: Number(this.$route.query.tab) || 0, // 탭 상태를 쿼리에서 복원(뒤로가기 유지)
      accessories,
      foods: [], // item 테이블(type=FOOD)에서 로드 (노출만)
      toys: [], // item 테이블(type=TOY)
      houses,
      backgrounds: [], // item 테이블(type=BACKGROUND) — 라이브 lottie 미리보기, 보유 기반
      ownedMap: {}, // { [itemId]: qty } 내 보유 인벤토리
      buyDialog: false,
      buyTarget: null,
      buyQty: 1,
      buying: false,
    };
  },
  async created() {
    const [foods, toys, backgrounds] = await Promise.all([
      SupabaseApi.Item.fetchByType("FOOD"),
      SupabaseApi.Item.fetchByType("TOY"),
      SupabaseApi.Item.fetchByType("BACKGROUND"),
    ]);
    this.foods = foods;
    this.toys = toys;
    // 배경은 비-스택(1개 소유) → 다이얼로그 수량 UI 숨김용 플래그
    this.backgrounds = backgrounds.map((b) => ({ ...b, isBackground: true }));
    this.$store.dispatch("loadCurrencyBalance");
    this.ownedMap = await SupabaseApi.Item.fetchOwnedQtyMap();
  },
  watch: {
    // 탭 전환 → 쿼리 동기화(뒤로가기 시 이전 탭 복원)
    tab(n) {
      this.$router.replace({ query: { ...this.$route.query, tab: String(n) } }).catch(() => {});
    },
  },
  computed: {
    ...mapState(["currencyBalance"]),
    // 다이얼로그: 합계 금액 / 살 수 있는 최대 수량(잔액 기준, 1~99)
    totalCost() {
      return this.buyTarget ? this.buyTarget.price * this.buyQty : 0;
    },
    maxQty() {
      if (!this.buyTarget || this.buyTarget.price <= 0) return 99;
      return Math.max(1, Math.min(99, Math.floor(this.currencyBalance / this.buyTarget.price)));
    },
    accessoryItems() {
      return accessories.map((file, i) => ({
        file,
        src: `/demo/accessories/${encodeURIComponent(file)}`,
        name: this.displayName(file, i),
        price: 500 + ((i * 137) % 20) * 100, // 데모용 가격
      }));
    },
  },
  methods: {
    displayName(file, i) {
      const base = file.replace(/\.[^.]+$/, "");
      const pack = base.match(/hat-pack-?(\d+)/i);
      if (pack) return `카툰 모자 팩${pack[1]}`;
      if (/unicorn-hat/i.test(base)) return "유니콘 모자";
      return `악세사리 ${i + 1}`;
    },
    onBuyClick(item) {
      if (this.currencyBalance < item.price) return;
      this.buyTarget = item;
      this.buyQty = 1; // 기본 수량 1
      this.buyDialog = true;
    },
    changeQty(delta) {
      const next = this.buyQty + delta;
      this.buyQty = Math.max(1, Math.min(this.maxQty, next));
    },
    async confirmBuy() {
      if (!this.buyTarget || this.buying) return;
      this.buying = true;
      try {
        const { balance, qty } = await CurrencyApi.buyItem(this.buyTarget.id, this.buyQty);
        this.$store.commit("__setCurrencyBalance", balance);
        this.$set(this.ownedMap, this.buyTarget.id, qty); // 보유 배지 반응형 갱신
        if (this.$notify) this.$notify({ type: "success", title: "구입했어요!", duration: 2000 });
        this.buyDialog = false;
      } catch (e) {
        console.error("buyItem failed", e);
        const title =
          (e && e.detail && e.detail.message) === "E_INSUFFICIENT_BALANCE" ? "🥕가 부족해요" : "구매에 실패했어요";
        if (this.$notify) this.$notify({ type: "warning", title, duration: 2400 });
      } finally {
        this.buying = false;
      }
    },
  },
};
</script>

<style scoped>
.page-root {
  padding: 24px 28px;
}

.store-empty {
  padding: 48px 0;
  text-align: center;
  color: #9e93b8;
  font-size: 15px;
  font-weight: 600;
}

.store-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
  padding-top: 4px;
}

.store-card {
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.15s, box-shadow 0.15s;
}
.store-card:hover {
  transform: translateY(-4px);
}

.store-card__img {
  background-color: #f4f5fa;
}

/* 썸네일 래퍼 (보유 배지 오버레이용) */
.store-card__thumb {
  position: relative;
}
/* 배경 카드: 라이브 lottie 미리보기 박스 */
.bg-thumb {
  height: 120px;
  background: #fff;
  overflow: hidden;
}
.store-card__owned {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(42, 37, 64, 0.82);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
}
.store-card__buy {
  padding: 0 12px 12px;
}

/* 구매 다이얼로그 금액 줄 */
.buy-line {
  font-size: 14px;
  padding: 2px 0;
}
.remain-ok {
  color: #8e6fd6;
}

/* 음식 이모지 썸네일 */
.food-emoji {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  font-size: 64px;
  background: linear-gradient(135deg, #fff7ee, #ffeedd);
}

/* 집/배경화면 이모지 썸네일 (이미지 에셋 준비 전 플레이스홀더) */
.emoji-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  font-size: 64px;
  background: linear-gradient(135deg, #f1ecff, #e6def8);
}
.emoji-thumb--bg {
  background: linear-gradient(135deg, #e8f3ff, #dbe9fb);
}
.emoji-thumb--toy {
  background: linear-gradient(135deg, #fff1f5, #ffe0ec);
}
.toy-durability {
  font-size: 12px;
  font-weight: 700;
  color: #7a8b99;
}

.store-card__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
}
.store-card__name {
  font-size: 14px;
  font-weight: 600;
  color: #2a2540;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 다크모드: 짙은 남색은 배경과 묻혀서 밝게 */
.theme--dark .store-card__name {
  color: #eceff4;
}
.food-restore {
  display: flex;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  margin-top: 2px;
}
.food-restore__hunger {
  color: #e8743b;
}
.food-restore__energy {
  color: #d9a407;
}
.store-card__price {
  font-size: 13px;
  font-weight: 700;
  color: #8e6fd6;
  flex-shrink: 0;
  margin-left: 8px;
}

/* 음식 카드 하단 설명 */
.store-card__desc {
  padding: 0 12px 10px;
  margin-top: -2px;
  font-size: 12px;
  color: #8a8597;
  line-height: 1.4;
}
</style>
